import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AgentKind,
  AgentLaunchOptions,
  ApprovalRequest,
  CodexHistoryEntry,
  ConversationAgent,
  GitStatus,
  MachineInfo,
  ProjectInfo,
  SessionInfo,
} from '@niuma/protocol';
import { HubConnection, type ConnectionStatus } from './lib/connection.js';
import { HubApi } from './lib/api.js';
import { OutputBus } from './lib/output-bus.js';
import { MachineSwitcher } from './components/MachineSwitcher.js';
import { ProjectPicker } from './components/ProjectPicker.js';
import { TerminalView } from './components/TerminalView.js';
import { ConversationView } from './components/ConversationView.js';
import { DiffReview } from './components/DiffReview.js';
import { stringToBase64 } from './lib/base64.js';

const TOKEN_KEY = 'niuma.console.token';
const AGENT_SETTINGS_KEY = 'niuma.console.agentSettings';
const AGENT_SETTINGS_DEFAULT_MODEL_KEY = 'niuma.console.agentSettings.defaultModel';
const PREVIOUS_DEFAULT_CODEX_MODEL = 'gpt-5.6-sol';

type View = 'folders' | 'detail' | 'terminal';
type DetailTab = 'sessions' | 'changes';
type ReasoningEffort = NonNullable<AgentLaunchOptions['reasoningEffort']>;
type ServiceTier = NonNullable<AgentLaunchOptions['serviceTier']>;

interface AgentSettings {
  model: string;
  reasoningEffort: ReasoningEffort;
  serviceTier: ServiceTier;
}

const DEFAULT_AGENT_SETTINGS: AgentSettings = {
  model: 'gpt-6-astra',
  reasoningEffort: 'medium',
  serviceTier: 'auto',
};

const CODEX_MODEL_OPTIONS = [
  'gpt-6-astra',
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  'gpt-5.5',
  'gpt-5.4',
  'gpt-5.4-mini',
  'gpt-5.3-codex-spark',
] as const;

const CODEX_REASONING_OPTIONS: ReasoningEffort[] = ['low', 'medium', 'high', 'xhigh'];
const CODEX_MODEL_PICKER_DELAY_MS = 220;
const MOBILE_KEYBOARD_OVERLAY_GUARD_PX = 76;
const CODEX_MODEL_PICKER_MODEL_INDEX = new Map(
  CODEX_MODEL_OPTIONS.map((model, index) => [model, index]),
);
const CODEX_REASONING_PICKER_INDEX = new Map(
  CODEX_REASONING_OPTIONS.map((effort, index) => [effort, index]),
);
const CODEX_MODEL_DEFAULT_REASONING = new Map<(typeof CODEX_MODEL_OPTIONS)[number], ReasoningEffort>([
  ['gpt-6-astra', 'medium'],
  ['gpt-5.6-sol', 'low'],
  ['gpt-5.6-terra', 'medium'],
  ['gpt-5.6-luna', 'medium'],
  ['gpt-5.5', 'medium'],
  ['gpt-5.4', 'medium'],
  ['gpt-5.4-mini', 'medium'],
  ['gpt-5.3-codex-spark', 'high'],
]);
const ANSI_PATTERN = /\x1b\[[0-9;?]*[ -/]*[@-~]|\x1b\][^\x07]*(?:\x07|\x1b\\)/g;

export function App(): JSX.Element {
  useVisualViewportLayout();

  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  if (!token) {
    return (
      <TokenGate
        onSubmit={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }
  return (
    <Console
      token={token}
      onSignOut={() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }}
    />
  );
}

function useVisualViewportLayout(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const root = document.documentElement;
    let animationFrame: number | undefined;
    const timers = new Set<number>();

    const syncViewport = (): void => {
      root.style.setProperty('--visual-viewport-height', `${viewport.height}px`);
      root.style.setProperty('--visual-viewport-offset-top', `${viewport.offsetTop}px`);
      const needsKeyboardGuard =
        window.matchMedia('(hover: none) and (pointer: coarse)').matches &&
        isTextEntryElement(document.activeElement);
      root.style.setProperty(
        '--keyboard-overlay-guard',
        `${needsKeyboardGuard ? MOBILE_KEYBOARD_OVERLAY_GUARD_PX : 0}px`,
      );
    };

    const scheduleSync = (): void => {
      syncViewport();
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(syncViewport);
    };

    const syncThroughKeyboardAnimation = (): void => {
      scheduleSync();
      for (const delay of [100, 300, 600]) {
        const timer = window.setTimeout(() => {
          timers.delete(timer);
          scheduleSync();
        }, delay);
        timers.add(timer);
      }
    };

    syncViewport();
    viewport.addEventListener('resize', scheduleSync);
    viewport.addEventListener('scroll', scheduleSync);
    window.addEventListener('orientationchange', syncThroughKeyboardAnimation);
    document.addEventListener('focusin', syncThroughKeyboardAnimation);
    document.addEventListener('focusout', syncThroughKeyboardAnimation);

    return () => {
      viewport.removeEventListener('resize', scheduleSync);
      viewport.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('orientationchange', syncThroughKeyboardAnimation);
      document.removeEventListener('focusin', syncThroughKeyboardAnimation);
      document.removeEventListener('focusout', syncThroughKeyboardAnimation);
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
      for (const timer of timers) window.clearTimeout(timer);
      root.style.removeProperty('--visual-viewport-height');
      root.style.removeProperty('--visual-viewport-offset-top');
      root.style.removeProperty('--keyboard-overlay-guard');
    };
  }, []);
}

function isTextEntryElement(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  return (
    element instanceof HTMLTextAreaElement ||
    (element instanceof HTMLInputElement &&
      !['button', 'checkbox', 'file', 'radio', 'range', 'submit'].includes(element.type)) ||
    element.isContentEditable
  );
}

function TokenGate({ onSubmit }: { onSubmit: (token: string) => void }): JSX.Element {
  const [value, setValue] = useState('');
  return (
    <div className="gate-wrap">
      <form
        className="gate"
        onSubmit={(event) => {
          event.preventDefault();
          if (value.trim()) onSubmit(value.trim());
        }}
      >
        <div>
          <p className="eyebrow">agent console</p>
          <h1>remote-niuma</h1>
        </div>
        <label className="field-label" htmlFor="console-token">
          Access token
        </label>
        <input
          id="console-token"
          type="password"
          value={value}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Paste token"
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
        <button className="primary block" type="submit" disabled={!value.trim()}>
          Connect
        </button>
      </form>
    </div>
  );
}

function Console({ token, onSignOut }: { token: string; onSignOut: () => void }): JSX.Element {
  const bus = useMemo(() => new OutputBus(), []);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [machines, setMachines] = useState<MachineInfo[]>([]);
  const [selectedMachineId, setSelectedMachineId] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [codexHistory, setCodexHistory] = useState<CodexHistoryEntry[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [gitStatus, setGitStatus] = useState<GitStatus | null>(null);
  const [diff, setDiff] = useState<string | null>(null);
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('folders');
  const [detailTab, setDetailTab] = useState<DetailTab>('sessions');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [agentSettings, setAgentSettings] = useState<AgentSettings>(() => loadAgentSettings());
  const [liveSessionSettings, setLiveSessionSettings] = useState<Map<string, AgentSettings>>(
    () => new Map(),
  );

  const connRef = useRef<HubConnection | null>(null);
  const apiRef = useRef<HubApi | null>(null);
  const loadedMachineSeenRef = useRef(new Map<string, number>());
  const outputTextRef = useRef(new Map<string, string>());

  useEffect(() => {
    const conn = new HubConnection(token, {
      onStatus: setStatus,
      onMachines: setMachines,
      onOutput: (_machineId, sessionId, bytes) => {
        bus.emit(sessionId, bytes);
        const parsed = parseCodexStatusSettings(sessionId, bytes, outputTextRef.current);
        if (parsed) {
          setLiveSessionSettings((prev) => {
            const current = prev.get(sessionId);
            if (
              current?.model === parsed.model &&
              current.reasoningEffort === parsed.reasoningEffort &&
              current.serviceTier === parsed.serviceTier
            ) {
              return prev;
            }
            const next = new Map(prev);
            next.set(sessionId, parsed);
            return next;
          });
        }
      },
      onExit: (_machineId, sessionId, exitCode) => {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        setLiveSessionSettings((prev) => {
          if (!prev.has(sessionId)) return prev;
          const next = new Map(prev);
          next.delete(sessionId);
          return next;
        });
        outputTextRef.current.delete(sessionId);
        setActiveSessionId((cur) => {
          if (cur !== sessionId) return cur;
          setView('detail');
          setError(`Session exited${exitCode === null ? '' : ` with code ${exitCode}`}.`);
          return null;
        });
      },
      onApproval: (request) => setApprovals((prev) => [...prev, request]),
    });
    connRef.current = conn;
    apiRef.current = new HubApi(conn);
    conn.connect();
    return () => conn.close();
  }, [token, bus]);

  const selectedMachine = machines.find((m) => m.id === selectedMachineId) ?? null;

  // Auto-select the first online machine once available.
  useEffect(() => {
    if (selectedMachineId && machines.some((m) => m.id === selectedMachineId)) return;
    const first = machines.find((m) => m.online) ?? machines[0];
    if (first) setSelectedMachineId(first.id);
  }, [machines, selectedMachineId]);

  const loadMachine = useCallback(async (machineId: string) => {
    const api = apiRef.current;
    if (!api) return;
    setBusy(true);
    setError(null);
    try {
      const [projectList, sessionList] = await Promise.all([
        api.listProjects(machineId),
        api.listSessions(machineId),
      ]);
      setProjects(projectList);
      setSessions(sessionList);
      setCodexHistory([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedMachineId || !selectedMachine) return;
    const previous = loadedMachineSeenRef.current.get(selectedMachineId);
    loadedMachineSeenRef.current.set(selectedMachineId, selectedMachine.lastSeen);
    if (previous === undefined || previous === selectedMachine.lastSeen) return;
    void loadMachine(selectedMachineId);
  }, [loadMachine, selectedMachine, selectedMachineId]);

  useEffect(() => {
    if (selectedMachineId) {
      setSelectedProject(null);
      setGitStatus(null);
      setDiff(null);
      setCodexHistory([]);
      setView('folders');
      void loadMachine(selectedMachineId);
    }
  }, [selectedMachineId, loadMachine]);

  const createProject = useCallback(
    async (name: string, initGit: boolean, addReadme: boolean) => {
      const api = apiRef.current;
      if (!api || !selectedMachineId) return;
      setBusy(true);
      setError(null);
      try {
        const project = await api.createProject(selectedMachineId, name, initGit, addReadme);
        setProjects((prev) => [project, ...prev.filter((p) => p.name !== project.name)]);
        setSelectedProject(project.name);
        setDetailTab('sessions');
        setView('detail');
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [selectedMachineId],
  );

  const openProject = useCallback((name: string | null) => {
    setSelectedProject(name);
    setDetailTab('sessions');
    setView('detail');
  }, []);

  const startSession = useCallback(
    async (agent: AgentKind) => {
      const api = apiRef.current;
      if (!api || !selectedMachineId) return;
      setBusy(true);
      setError(null);
      try {
        const session = await api.createSession(
          selectedMachineId,
          agent,
          selectedProject ?? undefined,
          80,
          24,
          agent === 'codex' ? launchOptions(agentSettings) : undefined,
        );
        setSessions((prev) => [...prev, session]);
        setActiveSessionId(session.id);
        setView('terminal');
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [agentSettings, selectedMachineId, selectedProject],
  );

  const stopSession = useCallback(
    async (sessionId: string) => {
      const api = apiRef.current;
      if (!api || !selectedMachineId) return;
      await api.stopSession(selectedMachineId, sessionId).catch(() => undefined);
    },
    [selectedMachineId],
  );

  const refreshCodexHistory = useCallback(async () => {
    const api = apiRef.current;
    if (!api || !selectedMachineId || !selectedProject) {
      setCodexHistory([]);
      return;
    }
    setError(null);
    try {
      const history = await api.codexHistory(selectedMachineId, selectedProject, 60);
      setCodexHistory(history);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [selectedMachineId, selectedProject]);

  const resumeCodexHistory = useCallback(
    async (entry: CodexHistoryEntry) => {
      const api = apiRef.current;
      if (!api || !selectedMachineId) return;
      setBusy(true);
      setError(null);
      try {
        const session = await api.resumeCodex(
          selectedMachineId,
          entry.id,
          entry.cwd,
          entry.project ?? selectedProject ?? undefined,
          80,
          24,
          (entry.agent ?? 'codex') === 'codex' ? launchOptions(agentSettings) : undefined,
        );
        setSessions((prev) =>
          prev.some((existing) => existing.id === session.id) ? prev : [...prev, session],
        );
        setActiveSessionId(session.id);
        setView('terminal');
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [agentSettings, selectedMachineId, selectedProject],
  );

  const refreshGit = useCallback(async () => {
    const api = apiRef.current;
    if (!api || !selectedMachineId || !selectedProject) return;
    setBusy(true);
    setError(null);
    try {
      const [s, d] = await Promise.all([
        api.gitStatus(selectedMachineId, selectedProject),
        api.diff(selectedMachineId, selectedProject),
      ]);
      setGitStatus(s);
      setDiff(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [selectedMachineId, selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      void refreshGit();
      void refreshCodexHistory();
    }
  }, [selectedProject, refreshGit, refreshCodexHistory]);

  const decideApproval = (id: string, approved: boolean): void => {
    connRef.current?.decideApproval(id, approved);
    setApprovals((prev) => prev.filter((a) => a.id !== id));
  };

  const machineSessions = sessions.filter((s) => s.machineId === selectedMachineId);
  const liveSessions = machineSessions
    .filter((s) => (selectedProject ? s.project === selectedProject : !s.project))
    .sort((a, b) => b.createdAt - a.createdAt);

  // A running codex/claude session also has a transcript in its agent's session
  // store, so it appears in history with the real thread title. Correlate the
  // two by agent + working directory + start time: give the live row that title,
  // and hide the duplicate history row so each conversation appears once.
  const liveTitles = new Map<string, string>();
  const liveTranscriptIds = new Map<string, string>();
  const twinnedHistoryIds = new Set<string>();
  for (const session of liveSessions) {
    if (session.agent === 'shell') continue;
    let best: CodexHistoryEntry | undefined;
    let bestDelta = Number.POSITIVE_INFINITY;
    for (const entry of codexHistory) {
      if (twinnedHistoryIds.has(entry.id) || entry.cwd !== session.cwd) continue;
      if ((entry.agent ?? 'codex') !== session.agent) continue;
      if (session.historySessionId === entry.id) {
        best = entry;
        bestDelta = 0;
        break;
      }
      const delta = Math.abs(entry.createdAt - session.createdAt);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = entry;
      }
    }
    if (best && bestDelta < 5 * 60 * 1000) {
      twinnedHistoryIds.add(best.id);
      liveTitles.set(session.id, best.title);
      liveTranscriptIds.set(session.id, best.id);
    }
  }
  const visibleLiveSessions = liveSessions;
  const savedSessions = selectedProject
    ? codexHistory.filter((entry) => !twinnedHistoryIds.has(entry.id))
    : [];
  const sessionCount = visibleLiveSessions.length + savedSessions.length;
  const activeSession = machineSessions.find((s) => s.id === activeSessionId) ?? null;
  const activeConversationAgent =
    activeSession && isConversationAgent(activeSession.agent) ? activeSession.agent : null;
  const activeTranscriptId = activeSession
    ? (activeSession.historySessionId ?? liveTranscriptIds.get(activeSession.id))
    : undefined;
  const pendingApproval = approvals[0] ?? null;
  const selectedProjectInfo = projects.find((p) => p.name === selectedProject) ?? null;
  const folderLabel = selectedProject ?? 'Machine root';
  const folderKind = selectedProjectInfo?.isGitRepo
    ? 'Git repository'
    : selectedProject
      ? 'Folder'
      : 'Sessions outside any project';
  const canStart = !!selectedMachine?.online && !busy;

  const openSession = (sessionId: string): void => {
    setActiveSessionId(sessionId);
    setView('terminal');
  };

  let topBar: JSX.Element;
  if (view === 'detail' && selectedMachine) {
    topBar = (
      <header className="topbar">
        <div className="topbar-lead">
          <button className="nav-back" onClick={() => setView('folders')} aria-label="Back">
            ‹
          </button>
          <div className="topbar-heading">
            <span className="topbar-title">{folderLabel}</span>
            <span className="topbar-sub">{folderKind}</span>
          </div>
        </div>
        <span className={`status ${status}`}>{status}</span>
      </header>
    );
  } else if (view === 'terminal' && selectedMachine) {
    topBar = (
      <header className="topbar">
        <div className="topbar-lead">
          <button className="nav-back" onClick={() => setView('detail')} aria-label="Back">
            ‹
          </button>
          <div className="topbar-heading">
            <span className="topbar-title">
              {activeSession ? `${activeSession.agent} session` : 'Terminal'}
            </span>
            <span className="topbar-sub">{folderLabel}</span>
          </div>
        </div>
        <div className="topbar-actions">
          <button className="icon" onClick={() => setSettingsOpen(true)} aria-label="Settings">
            ⚙
          </button>
          {activeSession && (
            <button
              className="ghost danger"
              onClick={() => {
                void stopSession(activeSession.id);
                setView('detail');
              }}
            >
              Stop
            </button>
          )}
        </div>
      </header>
    );
  } else {
    topBar = (
      <header className="topbar">
        <div className="brand-block">
          <span className="brand">remote-niuma</span>
          <span className="topbar-sub">{selectedMachine?.name ?? 'No machine selected'}</span>
        </div>
        <div className="topbar-actions">
          {pendingApproval && <span className="approval-pill">Approval</span>}
          <span className={`status ${status}`}>{status}</span>
          <button className="ghost" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>
    );
  }

  return (
    <div className="app">
      {topBar}

      {error && (
        <div className="error" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {!selectedMachine ? (
        <div className="empty grow">No machines connected. Start a runner to register one.</div>
      ) : view === 'folders' ? (
        <main className="screen">
          <div className="screen-inner">
            <MachineSwitcher
              machines={machines}
              selectedId={selectedMachineId}
              onSelect={setSelectedMachineId}
            />

            <button className="folder-row root-row" onClick={() => openProject(null)}>
              <span className="folder-body">
                <span className="folder-name">Machine root</span>
                <span className="folder-meta">Sessions outside any project</span>
              </span>
              <span className="chev">›</span>
            </button>

            <ProjectPicker
              projects={projects}
              projectRoot={selectedMachine.projectRoot}
              selected={selectedProject}
              busy={busy}
              onSelect={openProject}
              onCreate={createProject}
              onRefresh={() => void loadMachine(selectedMachine.id)}
            />
          </div>
        </main>
      ) : view === 'detail' ? (
        <main className="screen">
          <div className="screen-inner">
            <div className="action-row">
              {selectedMachine.capabilities.map((agent) => (
                <button
                  key={agent}
                  className="primary"
                  disabled={!canStart}
                  onClick={() => void startSession(agent)}
                >
                  Start {agent}
                </button>
              ))}
            </div>

            <div className="segment">
              <button
                className={detailTab === 'sessions' ? 'active' : ''}
                onClick={() => setDetailTab('sessions')}
              >
                Sessions{sessionCount > 0 ? ` · ${sessionCount}` : ''}
              </button>
              <button
                className={detailTab === 'changes' ? 'active' : ''}
                disabled={!selectedProject}
                onClick={() => {
                  setDetailTab('changes');
                  void refreshGit();
                }}
              >
                Changes
              </button>
            </div>

            {detailTab === 'sessions' ? (
              <ul className="entry-list">
                {visibleLiveSessions.map((s) => (
                  <li key={s.id} className={s.id === activeSessionId ? 'active' : ''}>
                    <button className="entry" onClick={() => openSession(s.id)}>
                      <span className="entry-badge live">live</span>
                      <span className="entry-body">
                        <span className="entry-title">
                          {liveTitles.get(s.id) ?? liveSessionTitle(s)}
                        </span>
                        <span className="entry-meta">
                          {formatClock(s.createdAt)} · {formatCwd(s.cwd)}
                        </span>
                      </span>
                    </button>
                    <button
                      className="icon danger"
                      onClick={() => void stopSession(s.id)}
                      aria-label="Stop session"
                      title="Stop session"
                    >
                      ×
                    </button>
                  </li>
                ))}
                {savedSessions.map((e) => (
                  <li key={e.id}>
                    <button
                      className="entry"
                      disabled={busy || !selectedMachine.online}
                      onClick={() => void resumeCodexHistory(e)}
                    >
                      <span className="entry-badge saved">{e.agent ?? 'codex'}</span>
                      <span className="entry-body">
                        <span className="entry-title">{e.title}</span>
                        <span className="entry-meta">
                          {formatAge(e.updatedAt)} · {formatCwd(e.cwd)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
                {sessionCount === 0 && (
                  <li className="inline-empty">No sessions yet. Start codex or shell above.</li>
                )}
              </ul>
            ) : (
              <DiffReview
                status={gitStatus}
                diff={diff}
                busy={busy}
                onRefresh={() => void refreshGit()}
              />
            )}
          </div>
        </main>
      ) : (
        <main className="screen terminal-screen">
          {activeSession && apiRef.current && activeConversationAgent && activeTranscriptId ? (
            <ConversationView
              key={`${activeSession.id}:${activeTranscriptId}`}
              api={apiRef.current}
              bus={bus}
              machineId={selectedMachine.id}
              session={activeSession as SessionInfo & { agent: ConversationAgent }}
              transcriptId={activeTranscriptId}
              connectionStatus={status}
            />
          ) : activeSessionId && apiRef.current ? (
            <TerminalView
              key={activeSessionId}
              api={apiRef.current}
              bus={bus}
              machineId={selectedMachine.id}
              sessionId={activeSessionId}
              connectionStatus={status}
            />
          ) : (
            <div className="empty empty-terminal">
              <strong>No active terminal</strong>
              <span>Pick a session from the folder to continue here.</span>
            </div>
          )}
        </main>
      )}

      {settingsOpen && (
        <AgentSettingsDialog
          settings={agentSettings}
          onClose={() => setSettingsOpen(false)}
          onSave={(settings) => {
            localStorage.setItem(AGENT_SETTINGS_KEY, JSON.stringify(settings));
            setAgentSettings(settings);
            setSettingsOpen(false);
            const api = apiRef.current;
            if (api && selectedMachineId && activeSession?.agent === 'codex') {
              const currentSettings = liveSessionSettings.get(activeSession.id);
              if (currentSettings) {
                void applyCodexSettingsToLiveSession(
                  api,
                  selectedMachineId,
                  activeSession.id,
                  currentSettings,
                  settings,
                ).catch((e) => setError(e instanceof Error ? e.message : String(e)));
              } else {
                setError('Could not detect the current Codex model yet. Wait for the status line, then save again.');
              }
            }
          }}
        />
      )}

      {pendingApproval && <ApprovalDialog request={pendingApproval} onDecide={decideApproval} />}
    </div>
  );
}

function loadAgentSettings(): AgentSettings {
  const raw = localStorage.getItem(AGENT_SETTINGS_KEY);
  const storedDefaultModel = localStorage.getItem(AGENT_SETTINGS_DEFAULT_MODEL_KEY);
  localStorage.setItem(AGENT_SETTINGS_DEFAULT_MODEL_KEY, DEFAULT_AGENT_SETTINGS.model);
  if (!raw) return DEFAULT_AGENT_SETTINGS;
  try {
    const parsed = JSON.parse(raw) as Partial<AgentSettings>;
    const shouldUpgradePreviousDefault =
      storedDefaultModel !== DEFAULT_AGENT_SETTINGS.model &&
      parsed.model === PREVIOUS_DEFAULT_CODEX_MODEL;
    const settings = {
      model:
        !shouldUpgradePreviousDefault && isCodexModel(parsed.model)
          ? parsed.model
          : DEFAULT_AGENT_SETTINGS.model,
      reasoningEffort: isReasoningEffort(parsed.reasoningEffort)
        ? parsed.reasoningEffort
        : DEFAULT_AGENT_SETTINGS.reasoningEffort,
      serviceTier: isServiceTier(parsed.serviceTier)
        ? parsed.serviceTier
        : DEFAULT_AGENT_SETTINGS.serviceTier,
    };
    if (shouldUpgradePreviousDefault) {
      localStorage.setItem(AGENT_SETTINGS_KEY, JSON.stringify(settings));
    }
    return settings;
  } catch {
    return DEFAULT_AGENT_SETTINGS;
  }
}

function launchOptions(settings: AgentSettings): AgentLaunchOptions {
  return {
    model: settings.model.trim() || undefined,
    reasoningEffort: settings.reasoningEffort,
    serviceTier: settings.serviceTier,
  };
}

async function applyCodexSettingsToLiveSession(
  api: HubApi,
  machineId: string,
  sessionId: string,
  currentSettings: AgentSettings,
  settings: AgentSettings,
): Promise<void> {
  const currentModel = isCodexModel(currentSettings.model) ? currentSettings.model : null;
  const targetModel = isCodexModel(settings.model) ? settings.model : null;
  if (!currentModel || !targetModel) {
    throw new Error(`Cannot apply unsupported Codex model: ${settings.model}`);
  }
  const currentModelIndex = currentModel ? CODEX_MODEL_PICKER_MODEL_INDEX.get(currentModel) : undefined;
  const targetModelIndex = targetModel ? CODEX_MODEL_PICKER_MODEL_INDEX.get(targetModel) : undefined;
  if (currentModelIndex === undefined || targetModelIndex === undefined) {
    throw new Error(`Cannot apply unsupported Codex model: ${settings.model}`);
  }

  const currentReasoning =
    currentSettings.model === settings.model
      ? currentSettings.reasoningEffort
      : (CODEX_MODEL_DEFAULT_REASONING.get(targetModel) ?? 'medium');
  const currentReasoningIndex = CODEX_REASONING_PICKER_INDEX.get(currentReasoning) ?? 1;
  const targetReasoningIndex = CODEX_REASONING_PICKER_INDEX.get(settings.reasoningEffort) ?? 1;

  await sendCodexInput(api, machineId, sessionId, '/model\r');
  await wait(CODEX_MODEL_PICKER_DELAY_MS);
  await sendCodexInput(api, machineId, sessionId, '\r');
  await wait(CODEX_MODEL_PICKER_DELAY_MS);
  await sendCodexInput(api, machineId, sessionId, pickerMove(currentModelIndex, targetModelIndex));
  await wait(CODEX_MODEL_PICKER_DELAY_MS);
  await sendCodexInput(
    api,
    machineId,
    sessionId,
    pickerMove(currentReasoningIndex, targetReasoningIndex),
  );

  if (settings.serviceTier !== currentSettings.serviceTier) {
    await wait(CODEX_MODEL_PICKER_DELAY_MS);
    await sendCodexInput(api, machineId, sessionId, '/fast\r');
  }
}

async function sendCodexInput(
  api: HubApi,
  machineId: string,
  sessionId: string,
  input: string,
): Promise<void> {
  await api.sendInput(machineId, sessionId, stringToBase64(input));
}

function pickerMove(fromIndex: number, toIndex: number): string {
  const delta = toIndex - fromIndex;
  if (delta === 0) return '\r';
  const key = delta > 0 ? '\x1b[B' : '\x1b[A';
  return `${key.repeat(Math.abs(delta))}\r`;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function parseCodexStatusSettings(
  sessionId: string,
  bytes: Uint8Array,
  buffers: Map<string, string>,
): AgentSettings | null {
  const previous = buffers.get(sessionId) ?? '';
  const next = (previous + new TextDecoder().decode(bytes)).replace(ANSI_PATTERN, '');
  buffers.set(sessionId, next.slice(-3000));

  const modelAlternation = CODEX_MODEL_OPTIONS.map(escapeRegExp).join('|');
  const matches = [
    ...next.matchAll(
      new RegExp(
        `(${modelAlternation})\\s+(low|medium|high|xhigh|default)(?:\\s+(fast))?\\s+·`,
        'g',
      ),
    ),
  ];
  const match = matches.at(-1);
  const model = match?.[1];
  if (!model || !isCodexModel(model)) return null;

  return {
    model,
    reasoningEffort: match[2] === 'default' ? 'medium' : (match[2] as ReasoningEffort),
    serviceTier: match[3] === 'fast' ? 'fast' : 'auto',
  };
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isConversationAgent(agent: AgentKind): agent is ConversationAgent {
  return agent === 'codex' || agent === 'claude';
}

function isCodexModel(value: unknown): value is (typeof CODEX_MODEL_OPTIONS)[number] {
  return typeof value === 'string' && CODEX_MODEL_OPTIONS.some((model) => model === value);
}

function isReasoningEffort(value: unknown): value is ReasoningEffort {
  return value === 'low' || value === 'medium' || value === 'high' || value === 'xhigh';
}

function isServiceTier(value: unknown): value is ServiceTier {
  return value === 'auto' || value === 'fast';
}

function formatAge(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < hour) return `${Math.max(1, Math.floor(diff / minute))}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  return `${Math.floor(diff / day)}d ago`;
}

function formatClock(ms: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(ms);
}

function liveSessionTitle(session: SessionInfo): string {
  return `${session.agent} session`;
}

function formatCwd(cwd: string): string {
  const parts = cwd.split('/').filter(Boolean);
  return parts.slice(-2).join('/');
}

function AgentSettingsDialog({
  settings,
  onClose,
  onSave,
}: {
  settings: AgentSettings;
  onClose: () => void;
  onSave: (settings: AgentSettings) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(settings);
  return (
    <div className="modal-backdrop">
      <form
        className="modal settings-modal"
        onSubmit={(event) => {
          event.preventDefault();
          onSave({
            ...draft,
            model: draft.model.trim() || DEFAULT_AGENT_SETTINGS.model,
          });
        }}
      >
        <h2>Agent settings</h2>
        <label className="field-label" htmlFor="agent-model">
          Codex model
        </label>
        <select
          id="agent-model"
          value={draft.model}
          onChange={(event) => setDraft((prev) => ({ ...prev, model: event.target.value }))}
        >
          {CODEX_MODEL_OPTIONS.map((model) => (
            <option key={model} value={model}>
              {model}
            </option>
          ))}
        </select>

        <label className="field-label" htmlFor="agent-reasoning">
          Speed / reasoning
        </label>
        <select
          id="agent-reasoning"
          value={draft.reasoningEffort}
          onChange={(event) =>
            setDraft((prev) => ({
              ...prev,
              reasoningEffort: event.target.value as ReasoningEffort,
            }))
          }
        >
          <option value="low">Fast</option>
          <option value="medium">Balanced</option>
          <option value="high">Deep</option>
          <option value="xhigh">Extra deep</option>
        </select>

        <label className="field-label" htmlFor="agent-tier">
          Service tier
        </label>
        <select
          id="agent-tier"
          value={draft.serviceTier}
          onChange={(event) =>
            setDraft((prev) => ({ ...prev, serviceTier: event.target.value as ServiceTier }))
          }
        >
          <option value="auto">Auto</option>
          <option value="fast">Fast</option>
        </select>

        <p className="muted">
          Applies when starting or resuming Codex sessions. Running sessions keep their current
          process settings.
        </p>

        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function ApprovalDialog({
  request,
  onDecide,
}: {
  request: ApprovalRequest;
  onDecide: (id: string, approved: boolean) => void;
}): JSX.Element {
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <h2>Approval required</h2>
        <span className={`badge risk-${request.risk}`}>{request.risk}</span>
        <p className="action">{request.action}</p>
        <p className="muted">{request.reason}</p>
        <dl className="approval-context">
          <div>
            <dt>Machine</dt>
            <dd>{request.machineId}</dd>
          </div>
          {request.project && (
            <div>
              <dt>Project</dt>
              <dd>{request.project}</dd>
            </div>
          )}
          {request.cwd && (
            <div>
              <dt>Working dir</dt>
              <dd>{request.cwd}</dd>
            </div>
          )}
        </dl>
        <div className="modal-actions">
          <button className="danger" onClick={() => onDecide(request.id, false)}>
            Reject
          </button>
          <button className="primary" onClick={() => onDecide(request.id, true)}>
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
